import React, { useCallback, useState } from 'react';
import type { Nullable } from 'corgi-types';

import Alert from 'common/components/Alert';
import DynamicForm from 'common/components/DynamicForm';
import { Flex } from 'common/components/Flex';
import FormSubmitButton from 'common/components/Form/FormSubmitButton';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { DispatchState } from 'common/types/React';
import { addBreakLineTag, newlinesToLineBreaks } from 'common/utils/text';
import { FieldName } from '../../enums/form';
import { useFormGroups } from '../../hooks/form-groups';
import { useSubmitForm } from '../../hooks/submit-form';
import type { CheckBalanceFormAlert } from '../../types/alert';
import type { GiftCardStatus } from '../../types/gift-card';
import { startsWithLetter } from '../../utils/validation';
import { Text, Title } from '../Text';

interface FormProps {
  setGiftCardStatus: DispatchState<Nullable<GiftCardStatus>>;
}

const Form = ({ setGiftCardStatus }: FormProps) => {
  const { t } = useLocalization();
  const [isEpayGiftCard, setIsEpayGiftCard] = useState(false);
  const [alert, setAlert] = useState<CheckBalanceFormAlert>(null);

  const formGroups = useFormGroups({ isEpayGiftCard });
  const { isSuccess, isLoading, handleSubmit } = useSubmitForm({
    setAlert,
    setGiftCardStatus,
  });

  const description = newlinesToLineBreaks(t('h24_check_gc_balance_description')).map(
    addBreakLineTag
  );

  const onWatchedFieldChange = useCallback((fieldName: FieldName, value: Nullable<string>) => {
    if (fieldName === FieldName.Code) {
      setIsEpayGiftCard(startsWithLetter(value ?? ''));
    }
  }, []);

  return (
    <>
      <Title>{t('h24_check_gc_balance_title')}</Title>
      <Text>{description}</Text>

      <Flex width={1} maxWidth={['100%', '320px']} flexDirection="column">
        {alert && (
          <Alert mb="sp_16" type={alert.type}>
            {t(alert.message)}
          </Alert>
        )}

        <DynamicForm
          id="gift-card-balance-form"
          formGroups={formGroups}
          onSubmit={handleSubmit}
          fieldsToWatch={[FieldName.Code]}
          onWatchedFieldChange={onWatchedFieldChange}
          ariaLabel={t('h24_gift_card_balance_form_label')}
          renderSubmitButton={props => (
            <FormSubmitButton
              {...props}
              mt="sp_16"
              defaultButtonLabel={t('h24_gift_card_submit_btn_label')}
              isDisabled={props.isDisabled || isSuccess}
              isLoading={isLoading}
            />
          )}
        />
      </Flex>
    </>
  );
};

export default Form;
